/* Copyright (c) 2025 MOCA Systems, Inc.  All rights reserved. */
import { AbstractApplication } from '@appCanvasCommon/core/AbstractApplication';
import { CanvasAngularLand } from '../../../../app-data/interfaces/canvas-angular-land';
import { LayerPaths } from '@appPlanApplication/LayerPaths';
import { AbstractAreaDrawer } from '@appCanvasCommon/core/AbstractAreaDrawer';
import { EventMode } from '@appCanvasCommon/events/event-mode.enum';
import { DestroyOptions } from '@appCanvasCommon/core/DestroyOptions';
import { cssVar } from '@touchplan/css-var';
import { DisplayObject, Rectangle } from 'pixi.js-legacy';
import { IActiveColumn } from '../../../../modules/plan/services/active-column.service';
import { ActiveLine } from '../../../../app-data/models/ActiveLine';
import { combineLatest } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { ViewportChangedEvent } from '@appCanvasCommon/interfaces/camera.interface';
import { LEVEL_OF_DETAIL_TABLE_STRINGS, LevelOfDetail } from '@appPlanApplication/lod';
import { IPrintConfiguration } from '../../../../app-data/constants/plan-print.model';

const settings = {
	nonWorkDayColor: cssVar.grey2, // The color to use for the shaded columns
	disableAtDetailLevel: LEVEL_OF_DETAIL_TABLE_STRINGS.VERY_LOW, // If the LOD is zoomed out past this point, hide the shaded columns
};

export class ShadedColumnDrawer extends AbstractAreaDrawer {
	private _cachedColumns: IActiveColumn[] | null;
	private _cachedActiveLine: ActiveLine | null;
	/**
	 * A flag to override the LOD check so we force a draw. If we do not have this, the first tile during a print export
	 * will be hidden.
	 * @private
	 */
	private _isExporting: boolean = false;

	/**
	 * NonWorkDayDrawer handles drawing of the shaded columns
	 * @param _canvasAngularLand
	 * @param parentApplication
	 */
	constructor(private _canvasAngularLand: CanvasAngularLand, parentApplication: AbstractApplication) {
		super(parentApplication, LayerPaths.BACKGROUND_VISUALS_NON_WORK_COLUMNS);
		this._container.eventMode = EventMode.Disabled;
		// We always swap the ShadedColumnDrawer to be the first thing drawn each frame
		// This ensures that the non-work day drawer is behind the active space drawer so separator lines
		// draw above the shaded column area.
		if (this._container.parent) {
			this._container.parent.swapChildren(this._container, this._container.parent.getChildAt(0));
		}
		this.setupSubscribers();
	}

	/**
	 * Draws the shaded (non-workday) columns on the plan
	 * @param columns
	 */
	public draw(columns: IActiveColumn[] | null = this._cachedColumns): void {
		if (this.shouldHide(columns)) {
			this.hide();
			return;
		}
		// Parent draw clears our graphics drawing data and sets the container to visible
		super.draw();

		const cameraView: Rectangle = this.parentApplication.camera.view;

		columns.forEach((column: IActiveColumn) => {
			const isWorkDay: boolean = this._canvasAngularLand.projectWorkingDayService.isWorkDay(column.activeDay);

			if (!isWorkDay) {
				const screenX: number = this.parentApplication.camera.realToScreenX(column.startX);
				const width: number = Math.abs(
					this.parentApplication.camera.realToScreenX(column.finishX) -
						this.parentApplication.camera.realToScreenX(column.startX)
				);
				this._graphics.beginFill(settings.nonWorkDayColor);
				this._graphics.drawRect(screenX, 0, width, cameraView.height);
				this._graphics.endFill();
			}
		});

		/**
		 * This forces a redraw when the workDayByDow project data changes. The reason why we need to explicitly mark the parent as dirty
		 * is because the timeline is not part of the typical AbstractApplication update->draw flow. Instead, each timeline drawer
		 * will only draw when specific Observables emit; we want this otherwise some timeline elements will re-draw every tick.
		 *
		 * If this line is removed, then the non-workday shaded column will NOT "live" update in the following situation:
		 * - Authed project member's primary role ID does NOT have a separate role schedule (is using project work schedule)
		 * - You change the Project Schedule (Settings -> Project Work Days)
		 */
		this.parentApplication.markAsDirty();
	}

	/**
	 * Destroy all the things. We need to override the abstract destroy so we can clear up our objects specific to this model
	 * @param options
	 */
	public destroy(options: DestroyOptions = DestroyOptions.includeChildren): void {
		super.destroy(options);
		this._canvasAngularLand = null;
	}

	/**
	 * Setup all internal subscriptions that causes this drawer to re-draw
	 * This is _slightly_ different than the ActiveSpaceDrawer where we want to collect _all_ columns in the viewport, not a culled list.
	 * The intention here is to always draw the shaded columns, even if they are potentially culled by the culling step.
	 * If we used the "visible columns" here, we would only draw the shaded columns if they were NOT culled by the culling step
	 * @private
	 */
	private setupSubscribers(): void {
		combineLatest([
			this.parentApplication.camera.changed$,
			this._canvasAngularLand.activeColumnService.changed$,
			this._canvasAngularLand.activeLineService.changed$,
		])
			.pipe(takeUntil(this._shutdownSubject$))
			.subscribe(
				([changedEvent, allColumns, activeLine]: [ViewportChangedEvent, IActiveColumn[], ActiveLine]) => {
					this._cachedActiveLine = activeLine;
					this._cachedColumns = allColumns;
					this.draw(allColumns);
				}
			);

		this._canvasAngularLand?.planPrintService.changed$
			.pipe(takeUntil(this._shutdownSubject$))
			.subscribe((config: IPrintConfiguration | null) => {
				if (config) {
					this._isExporting = true;
					return;
				}

				this._isExporting = false;
			});
	}

	/**
	 * Helper method to check if we should draw the columns or hide. In this case, we hide when one of the following is met:
	 * - input columns is null or an empty array (length of 0)
	 * - There is no active line (assume no active line = no active space)
	 * - The current Level of Detail is set to VERY_LOW
	 * @param columns
	 * @private
	 */
	private shouldHide(columns: IActiveColumn[] | null): boolean {
		return (
			!columns ||
			!columns.length ||
			!this._cachedActiveLine ||
			!this._cachedActiveLine.view.activeLineX === undefined ||
			(LevelOfDetail.currentLOD === settings.disableAtDetailLevel && !this._isExporting)
		);
	}
}
